import { next, schedule } from '@ember/runloop';

import { modifier } from 'ember-modifier';
import $ from 'jquery';

import { PopupCloseReason } from 'webapp/enums/enums';
import { uniqueId } from 'webapp/utils/common';

import type PopupBase from 'webapp/models-custom/popup/base';

type OpenPopupFn = (node: HTMLElement) => PopupBase | Promise<PopupBase | null> | null | void;

type CheckIfChildPopupIsOpen = () => boolean;

const onHoverPopup = (
	element: HTMLElement,
	[openPopupFn, mouseLeaveDelay = 0, checkIfChildPopupIsOpen]: [
		OpenPopupFn,
		number?,
		CheckIfChildPopupIsOpen?,
	],
) => {
	const parentElement = element.parentNode as HTMLElement;

	let popupModel: PopupBase | null = null,
		popupNode: HTMLElement | null = null;

	const checkToClose = function (mousePos: { x: number; y: number }) {
		if (!popupModel) {
			return;
		}

		// in case when we need to leave the main popup open
		// we need to check if the child popup is open
		// https://app.asana.com/1/7582066485656/project/1201898938264596/task/1209907696549752?focus=true
		if (checkIfChildPopupIsOpen?.()) {
			return;
		}

		if (typeof mousePos.x !== 'number' || typeof mousePos.y !== 'number') {
			return;
		}

		// don't close if over trigger area or popup itself
		const overNode = document.elementFromPoint(mousePos.x, mousePos.y) as HTMLElement,
			overElement = $(overNode),
			triggerCt = $(element),
			dontCloseCt = $(parentElement),
			popupCt = $(popupNode!),
			isOverTrigger =
				element && (triggerCt.is(overElement) || triggerCt.has(overNode).length > 0),
			isOverPopup =
				popupNode &&
				(popupCt.is(overElement) ||
					popupCt.has(overNode).length > 0 ||
					popupNode.matches(':hover')),
			isOverDontClose =
				parentElement &&
				(dontCloseCt.is(overElement) || dontCloseCt.has(overNode).length > 0);

		if (
			!popupNode &&
			(popupModel?.isOpening || popupModel?.isOpen) &&
			overElement.is('.popup.context-popup')
		) {
			// there is the moment where popup element has been inserted and we're hovering it, but node is not set on the model yet
			// skip the closing then
			return;
		}

		if (!isOverTrigger && !isOverPopup && !isOverDontClose) {
			void popupModel.close(PopupCloseReason.OUTSIDECLICK);
			popupModel = null;
			popupNode = null;
		}
	};

	const bindLeaveEvents = function (el: HTMLElement) {
		if (!el) {
			return;
		}

		// use jquery bcos of event namespacing & easier unbinding so we don't have to keep references to all events
		$(el)
			.off('mouseleave.popup-on-hover touchend.popup-on-hover')
			.on('mouseleave.popup-on-hover touchend.popup-on-hover', (eOut) => {
				let mousePos = { x: eOut.clientX, y: eOut.clientY };
				const uuid = uniqueId();

				if (mouseLeaveDelay) {
					$(document).on('mousemove.' + uuid, (eMove) => {
						// eslint-disable-next-line ember/jquery-ember-run
						mousePos = { x: eMove.pageX!, y: eMove.pageY! };
					});

					setTimeout(() => {
						$(document).off('mousemove.' + uuid);
						checkToClose(mousePos);
					}, mouseLeaveDelay);
				} else {
					checkToClose(mousePos);
				}
			});
	};

	const openPopup = function () {
		const promiseOrPopup = openPopupFn(element);

		if (!promiseOrPopup) {
			return;
		}

		let promise;
		if (promiseOrPopup instanceof Promise) {
			promise = promiseOrPopup;
		} else {
			promise = Promise.resolve(promiseOrPopup);
		}

		void promise.then((popup) => {
			popupModel = popup;

			if (!popup) {
				return;
			}

			next(() => {
				schedule('afterRender', () => {
					const popupComponent = popupModel?.component;

					popupNode = popupComponent?.popupNode || null;

					if (popupNode) {
						bindLeaveEvents(popupNode);
					}
				});
			});
		});
	};

	element.addEventListener('mouseenter', openPopup);
	element.addEventListener('touchstart', openPopup);

	bindLeaveEvents(element);
	bindLeaveEvents(parentElement);

	return () => {
		element.removeEventListener('mouseenter', openPopup);
		element.removeEventListener('touchstart', openPopup);
		$(element).off('.popup-on-hover');
		$(parentElement).off('.popup-on-hover');
		if (popupNode) {
			$(popupNode).off('.popup-on-hover');
		}
	};
};

const onHoverPopupModifier = modifier(onHoverPopup);

export default onHoverPopupModifier;
